import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MainLayout } from "../layout/mainLayout";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { PagedResult } from "../../models/pagedResult/pagedResult";
import {
    TransportOrderNotificationListItemDto,
    UnconfirmedNotificationStates,
} from "../../models/transport/transportOrderNotification";
import { fetchAllTransportOrderNotifications } from "../../services/transportOrderService";
import { getDesktopNotifications } from "../../services/notificationService";
import { useInterval } from "usehooks-ts";
import { NotificationsTable } from "./NotificationsTable";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { fetchEmployees } from "../../store/employeeSlice";
import { NotificationListItemDto } from "../../models/notification/notification";
import { NotificationSource } from "../../models/notification/notificationSource";
import { showApiError } from "../framework/formUtils";

const REFETCH_INTERVAL = 1000 * 60;

export const NotificationsListMain = () => {
    const dispatch = useAppDispatch();
    const ownerParameters = new OwnerParameters();
    const hasTransportOrdersEnabled =
        ownerParameters.getOwnerHasTransportEnabled();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { t } = useTranslation();

    const employees = useAppSelector((state) => state.employees.employees);
    const [notifications, setNotifications] = useState<
        TransportOrderNotificationListItemDto[]
    >([]);
    const [desktopNotifications, setDesktopNotifications] = useState<
        NotificationListItemDto[]
    >([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);

    const fetchTransportOrderNotifications = useCallback(
        async(
            sortField?: string,
            sortOrder?: string
        ): Promise<PagedResult<TransportOrderNotificationListItemDto>> => {
            setIsLoading(true);

            try {
                const result = await fetchAllTransportOrderNotifications({
                    source: NotificationSource.MobileToDesktop,
                    states: UnconfirmedNotificationStates,
                    page: currentPage,
                    pageSize: pageSize,
                    sortField: sortField ? sortField : null,
                    sortOrder: sortOrder ? sortOrder : null,
                });

                setNotifications(result?.items);
                setTotalCount(result?.totalCount);

                return result;
            } catch {
                showApiError(t("notifications.fetchFailed"));

                return {
                    items: [],
                    currentPage: 1,
                    pageSize: 50,
                    totalCount: 0,
                    totalPages: 0,
                    hasPreviousPage: false,
                    hasNextPage: false,
                };
            } finally {
                setIsLoading(false);
            }
        },
        [currentPage, pageSize, t]
    );

    const fetchDesktopNotificationsData = useCallback(async() => {
        try {
            const result = await getDesktopNotifications();
            setDesktopNotifications(result ?? []);
        } catch {
            showApiError(t("notifications.fetchFailed"));
        }
    }, [t]);

    useInterval(() => {
        fetchTransportOrderNotifications().catch((err) => {
            console.error("Error during scheduled fetch:", err);
        });
        void fetchDesktopNotificationsData();
    }, REFETCH_INTERVAL);

    useEffect(() => {
        fetchTransportOrderNotifications().catch(() => {
            showApiError(t("notifications.fetchFailed"));
        });
        void fetchDesktopNotificationsData();
    }, [currentPage, pageSize, fetchTransportOrderNotifications, fetchDesktopNotificationsData]);

    useEffect(() => {
        if (employees.length === 0) {
            dispatch(fetchEmployees() as any);
        }
    }, [employees]);

    if (!employees) {
        return <LoadingIndicator tooltip={t("common.loading")} />;
    }

    return (
        <MainLayout noContainer>
            <>
                {isLoading && (
                    <LoadingIndicator tooltip={t("common.loading")} />
                )}
                <NotificationsTable
                    notifications={notifications}
                    desktopNotifications={desktopNotifications}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={setPageSize}
                    fetchNotifications={fetchTransportOrderNotifications}
                    onDesktopNotificationsChanged={fetchDesktopNotificationsData}
                    hasTransportOrdersEnabled={hasTransportOrdersEnabled}
                />
            </>
        </MainLayout>
    );
};
